import { useEffect, useRef, useState } from 'react';
import { Button } from '../../../components/Button';
import { Switch } from '../../../components/Switch';
import { ApiError } from '../../../services/api';

type LoadState =
  | { kind: 'loading' }
  /** The API answered 404: it predates this switch. */
  | { kind: 'unavailable' }
  | { kind: 'failed'; message: string }
  | { kind: 'ready'; checked: boolean };

const describeError = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? `${fallback} ${err.message}` : fallback;

export interface SavedSwitchSettingProps {
  /**
   * Reads the saved value. Must be a stable reference (a module-level function), since it is an
   * effect dependency and a fresh one each render would reload in a loop.
   */
  load: (signal: AbortSignal) => Promise<boolean>;
  /** Saves the new value and answers with what the server kept. */
  save: (checked: boolean, signal: AbortSignal) => Promise<boolean>;
  label: string;
  description: string;
  loadingText: string;
  loadErrorText: string;
  unavailableText: string;
  /** Prefix for the test ids and the error id: `-switch`, `-retry`, `-unavailable`, `-error`. */
  idPrefix: string;
}

/**
 * One global on/off setting that saves the moment it is flipped, like every other `Switch`.
 *
 * The switch moves at once so it answers the click; a failed save puts it back and says why. A
 * failed load offers a retry, and a 404 (an API that predates the setting) gets one plain line
 * rather than an error.
 */
export const SavedSwitchSetting = ({
  load: loadSetting,
  save,
  label,
  description,
  loadingText,
  loadErrorText,
  unavailableText,
  idPrefix,
}: SavedSwitchSettingProps) => {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveController = useRef<AbortController | null>(null);
  const errorId = `${idPrefix}-error`;

  useEffect(() => {
    const controller = new AbortController();
    loadSetting(controller.signal)
      .then((checked) => setLoad({ kind: 'ready', checked }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.statusCode === 404) {
          setLoad({ kind: 'unavailable' });
          return;
        }
        setLoad({ kind: 'failed', message: describeError(err, loadErrorText) });
      });
    return () => controller.abort();
  }, [loadSetting, loadErrorText, reloadToken]);

  useEffect(() => () => saveController.current?.abort(), []);

  const handleRetry = () => {
    setLoad({ kind: 'loading' });
    setReloadToken((token) => token + 1);
  };

  const handleChange = async (next: boolean) => {
    if (load.kind !== 'ready' || saving) return;
    const previous = load.checked;
    const controller = new AbortController();
    saveController.current = controller;
    setSaving(true);
    setSaveError(null);
    setLoad({ kind: 'ready', checked: next });
    try {
      const saved = await save(next, controller.signal);
      if (controller.signal.aborted) return;
      setLoad({ kind: 'ready', checked: saved });
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      setLoad({ kind: 'ready', checked: previous });
      setSaveError(describeError(err, 'Could not save the change, so nothing changed.'));
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };

  if (load.kind === 'loading') {
    return <p className="admin-state">{loadingText}</p>;
  }

  if (load.kind === 'unavailable') {
    return (
      <p className="admin-state" data-testid={`${idPrefix}-unavailable`}>
        {unavailableText}
      </p>
    );
  }

  if (load.kind === 'failed') {
    return (
      <div className="admin-error" role="alert">
        <span>{load.message}</span>
        <Button
          type="button"
          variant="secondary"
          onClick={handleRetry}
          data-testid={`${idPrefix}-retry`}
        >
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Switch
        label={label}
        description={description}
        checked={load.checked}
        disabled={saving}
        aria-describedby={saveError ? errorId : undefined}
        onChange={(event) => void handleChange(event.target.checked)}
        data-testid={`${idPrefix}-switch`}
      />
      {saveError && (
        <p className="admin-error" role="alert" id={errorId}>
          {saveError}
        </p>
      )}
    </div>
  );
};
